import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { makeStyles } from "@mui/styles";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import {
  Button,
  IconButton,
  Container,
  Card,
  Grid,
  CardContent,
} from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import * as XLSX from 'xlsx';
import Radio from '@mui/material/Radio';
import TablePagination from '@mui/material/TablePagination';
import { Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import GetAppIcon from '@mui/icons-material/GetApp';
import { PieChart } from '@mui/x-charts/PieChart';
import { BarChart } from '@mui/x-charts/BarChart';

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
  },
  root1: {
    width: '100%',
  },
}));

export default function GenericFeedback({ config, searchTerm, setSearchTerm }) {
  const { labType, apiEndpoint, title, dataFilter, allowedStatuses } = config;

  const [labdata, setlabdata] = useState([]);
  const [labid, setlabid] = useState([]);
  const [feedbackDate, setFeedbackDate] = useState([]);
  const [labFeedbackCount, setlabFeedbackCount] = useState([]);
  const [feedbackRatingData, setFeedbackRatingData] = useState([]);
  const [manageavgTrainerRating, setmanageAvgTrainerRating] = useState({});
  const [manageavgTrainingRating, setmanageAvgTrainingRating] = useState({});

  const [selectedLabName, setSelectedLabName] = useState('');
  const [selectedLabId, setSelectedLabId] = useState('');
  const [selectedInstance, setSelectedInstance] = useState([]);
  const [selectedFeedbackId, setSelectedFeedbackId] = useState('');

  const [currentStep, setCurrentStep] = useState(1);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [dialogOpen, setDialogOpen] = useState(false);

  const classes = useStyles();

  // Fetch initial lab data
  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}${apiEndpoint}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + localStorage.getItem("token"),
      },
    })
      .then((resp) => resp.json())
      .then((data) => {
        if (!data.detail) {
          const feedbackInstance = data.filter((item) => 
            allowedStatuses.includes(item.status)
          );
          const runningInstance = feedbackInstance.filter((item) => item.feedback === 'Yes');
          setlabdata(runningInstance);
          
          // Apply data filter if provided
          const filteredData = dataFilter ? runningInstance.filter(dataFilter) : runningInstance;
          const customFeedbackCount = filteredData.map(item => item.feedback_no);
          setlabFeedbackCount(customFeedbackCount);

          if (runningInstance.length > 0) {
            const dates = runningInstance.map(item => item.feedBack_date);
            const formattedDates = dates[0]?.split(',').map(date => date.trim()) || [];
            setFeedbackDate(formattedDates);
            
            const extractedCustomIds = filteredData.map(item => item.id);
            setlabid(extractedCustomIds);
          }
        }
      })
      .catch((error) => {
        console.error("Error fetching lab data:", error);
      });
  }, [apiEndpoint, dataFilter, allowedStatuses]);

  // Fetch feedback rating data when lab is selected
  useEffect(() => {
    const fetchFeedbackRatingData = async () => {
      if (!selectedLabId) return;

      const feedbackRatingData = [];
      const labIndex = labid.indexOf(selectedLabId);
      const labFeedback = labFeedbackCount[labIndex];

      if (!labFeedback) return;

      for (let j = 1; j <= labFeedback; j++) {
        try {
          const response = await fetch(
            `${process.env.REACT_APP_API_URL}/api/traineefeedbackdata/${selectedLabId}/${j}/${labType}`,
            {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
                Authorization: "Bearer " + localStorage.getItem("token"),
              },
            }
          );
          const data = await response.json();
          if (!data.detail && data.length > 0) {
            let totalTrainerRating = 0;
            let totalTrainingRating = 0;
            
            data.forEach((feedback) => {
              totalTrainerRating += parseInt(feedback.trainer_rating);
              totalTrainingRating += parseFloat(feedback.training_rating);
            });
            
            const avgTrainerRating = Math.round(totalTrainerRating / data.length);
            const avgTrainingRating = Math.round(totalTrainingRating / data.length);
            
            feedbackRatingData.push({
              feedbackCount: j,
              feedbackData: data,
              avgTrainerRating,
              avgTrainingRating,
            });

            // Update average ratings state
            setmanageAvgTrainerRating(prev => ({
              ...prev,
              [selectedLabId]: avgTrainerRating,
            }));
            setmanageAvgTrainingRating(prev => ({
              ...prev,
              [selectedLabId]: avgTrainingRating,
            }));
          }
        } catch (error) {
          console.error("Error fetching feedback data:", error);
        }
      }

      setFeedbackRatingData(feedbackRatingData);
    };

    fetchFeedbackRatingData();
  }, [selectedLabId, labid, labFeedbackCount, labType]);

  const handleDownload = (event, value) => {
    const lab_id = value.id;
    const dataToExport = [];
    
    fetch(`${process.env.REACT_APP_API_URL}/api/labfeedbackdata/${lab_id}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + localStorage.getItem("token"),
      },
    })
      .then((resp) => resp.json())
      .then((data) => {
        if (data.length === 0) {
          setDialogOpen(true);
        } else {
          data.forEach((item) => {
            for (let i = 1; i <= 10; i++) {
              const row = {
                Trainee_Name: item.trainee_name,
                FeedBack_No: item.feedback_no,
                Feedback_Date: item.feedback_date,
                Question: item[`q${i}`],
                Answer: item[`qa${i}`],
                Comment: item[`qc${i}`]
              };
              dataToExport.push(row);
            }
          });

          const workbook = XLSX.utils.book_new();
          const worksheet = XLSX.utils.json_to_sheet(dataToExport);
          XLSX.utils.book_append_sheet(workbook, worksheet, "Feedback Data");
          XLSX.writeFile(workbook, "Lab-Feedback.xlsx");
        }
      })
      .catch((error) => {
        console.error("Error downloading lab feedback:", error);
      });
  };

  const handleTraineeDownload = (event, value) => {
    const feedbackNumber = value.feedbackCount;
    const dataToExport = [];
    
    fetch(`${process.env.REACT_APP_API_URL}/api/traineefeedbackdata/${selectedLabId}/${feedbackNumber}/${labType}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + localStorage.getItem("token"),
      },
    })
      .then((resp) => resp.json())
      .then((data) => {
        if (data.length === 0) {
          setDialogOpen(true);
        } else {
          data.forEach((item) => {
            for (let i = 1; i <= 10; i++) {
              const row = {
                Trainee_Name: item.trainee_name,
                Feedback_Date: item.feedback_date,
                Question: item[`q${i}`],
                Answer: item[`qa${i}`],
                Comment: item[`qc${i}`]
              };
              dataToExport.push(row);
            }
          });

          const workbook = XLSX.utils.book_new();
          const worksheet = XLSX.utils.json_to_sheet(dataToExport);
          XLSX.utils.book_append_sheet(workbook, worksheet, "Feedback Data");
          XLSX.writeFile(workbook, "Feedback.xlsx");
        }
      })
      .catch((error) => {
        console.error("Error downloading trainee feedback:", error);
      });
  };

  const handleDataDownload = (event, value) => {
    const { lab_id, trainee_name, feedback_no, templateId } = value;
    const dataToExport = [];
    
    fetch(`${process.env.REACT_APP_API_URL}/api/feedbackalldata/${lab_id}/${trainee_name}/${feedback_no}/${templateId}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + localStorage.getItem("token"),
      },
    })
      .then((resp) => resp.json())
      .then((data) => {
        if (!data.detail) {
          data.forEach((item) => {
            for (let i = 1; i <= 10; i++) {
              const row = {
                Trainee_Name: item.trainee_name,
                Feedback_Date: item.feedback_date,
                Question: item[`q${i}`],
                Answer: item[`qa${i}`],
                Comment: item[`qc${i}`]
              };
              dataToExport.push(row);
            }
          });

          const workbook = XLSX.utils.book_new();
          const worksheet = XLSX.utils.json_to_sheet(dataToExport);
          XLSX.utils.book_append_sheet(workbook, worksheet, "Feedback Data");
          XLSX.writeFile(workbook, "Trainee-Feedback.xlsx");
        }
      })
      .catch((error) => {
        console.error("Error downloading feedback data:", error);
      });
  };

  const handleLabSelection = (labId, labname) => {
    setSelectedLabName(labname);
    setSelectedLabId(labId);
  };

  const handleFeedbackSelection = (feedbackCount) => {
    setSelectedFeedbackId(feedbackCount);
  };

  const filteredPccData = labdata.filter((instance) =>
    instance.name.toLowerCase().includes((searchTerm || "").toLowerCase())
  );

  const paginatedPccData = filteredPccData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Apply data filter to paginated data for display
  const displayData = dataFilter 
    ? paginatedPccData.filter(dataFilter)
    : paginatedPccData;
  const filteredDataForCount = dataFilter 
    ? filteredPccData.filter(dataFilter)
    : filteredPccData;

  const handleContinue = () => {
    setSelectedFeedbackId('');
    fetch(`${process.env.REACT_APP_API_URL}/api/selectedLabfeedbackdetails/${selectedLabId}/${labType}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + localStorage.getItem("token"),
      },
    })
      .then((resp) => resp.json())
      .then((data) => {
        if (data === "Data found") {
          setCurrentStep((prevStep) => prevStep + 1);
          const selectedInstance = labdata.filter((item) => item.name === selectedLabName);
          setSelectedInstance(selectedInstance);
        } else if (data === "No Feedback found") {
          setDialogOpen(true);
        }
      })
      .catch((error) => {
        console.error("Error checking feedback details:", error);
      });
  };

  const handleContinue1 = () => {
    setCurrentStep((prevStep) => prevStep + 1);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setCurrentStep(1);
    setSelectedLabName('');
    setSelectedLabId('');
    if (setSearchTerm) {
      setSearchTerm("");
    }
  };

  const handleDone = () => {
    setCurrentStep(1);
    setSelectedLabName('');
    setSelectedLabId('');
    if (setSearchTerm) {
      setSearchTerm("");
    }
  };

  const handleFBBackClick = () => {
    setCurrentStep((prevStep) => prevStep - 1);
    setSelectedFeedbackId('');
  };

  const renderStep1 = () => (
    <CardContent sx={{ pt: 0, pb: 2, px: 0 }}>
      <Grid container spacing={0}>
        <Container maxWidth={false} sx={{ px: { xs: 1, md: 2 } }}>
            <Grid container justify="center" spacing={0}>
              <Grid item xs={12}>
                <Card>
                  <Box sx={{ width: '100%', overflow: 'hidden', '&::-webkit-scrollbar': { display: 'none' }, scrollbarWidth: 'none' }}>
                    {displayData.length > 0 ? (
                      <>
                        <DataGrid
                          rows={displayData}
                          getRowId={(row) => row.id || row.name}
                          columns={[
                            {
                              field: 'radio', headerName: 'Select Lab', flex: 0.8, minWidth: 140, align: 'center', headerAlign: 'center',
                              renderCell: (params) => (
                                <Radio
                                  checked={selectedLabName === params.row.name}
                                  onChange={() => handleLabSelection(params.row.id, params.row.name)}
                                  value={params.row.name}
                                  name="labRadio"
                                  inputProps={{ 'aria-label': params.row.name }}
                                />
                              )
                            },
                            { field: 'id', headerName: 'Lab Id', flex: 0.6, minWidth: 100, align: 'center', headerAlign: 'center' },
                            { field: 'name', headerName: 'Lab Name', flex: 1, minWidth: 160, align: 'center', headerAlign: 'center' },
                            { field: 'number_of_trainee', headerName: 'No of Trainee', flex: 0.8, minWidth: 140, align: 'center', headerAlign: 'center' },
                            { field: 'feedback_no', headerName: 'No of Feedback', flex: 0.8, minWidth: 140, align: 'center', headerAlign: 'center' },
                            {
                              field: 'download', headerName: 'Download', flex: 0.5, minWidth: 90, align: 'center', headerAlign: 'center',
                              renderCell: (params) => (
                                <Tooltip title="Download lab feedback (XLSX)">
                                  <span>
                                    <IconButton onClick={(event) => handleDownload(event, params.row)} sx={{ borderRadius: '50px' }}>
                                      <GetAppIcon />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              )
                            },
                          ]}
                          autoHeight
                          hideFooter={true}
                          hideFooterPagination={true}
                          sx={{
                            width: '100%',
                            '& .MuiDataGrid-virtualScroller': { overflowX: 'hidden', overflowY: 'hidden' },
                            '& .MuiDataGrid-main': { overflow: 'hidden' },
                          }}
                        />
                        <Box sx={{ display: 'flex', justifyContent: 'flex-start', pl: { xs: 1, md: 2 } }}>
                          <TablePagination
                            component="div"
                            count={filteredDataForCount.length}
                            page={page}
                            rowsPerPage={rowsPerPage}
                            onPageChange={(event, newPage) => setPage(newPage)}
                            onRowsPerPageChange={(event) => {
                              setRowsPerPage(parseInt(event.target.value, 10));
                              setPage(0);
                            }}
                          />
                        </Box>
                      </>
                    ) : (
                      <Typography align="center" variant="body1">No Data Available</Typography>
                    )}
                  </Box>
                </Card>
              </Grid>

              <Grid container justify="center" spacing={0} style={{ marginTop: "10px", marginBottom: "10px" }}>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Tooltip title={selectedLabName ? "Proceed to select feedback" : "Select a lab to continue"}>
                      <span>
                        <Button
                          variant="contained"
                          color="primary"
                          type="button"
                          style={{ marginTop: '20px' }}
                          disabled={!selectedLabName}
                          onClick={handleContinue}
                          sx={{ borderRadius: '50px' }}
                        >
                          Continue
                        </Button>
                      </span>
                    </Tooltip>
                  </Box>
                </Grid>
              </Grid>
              <Dialog open={dialogOpen} onClose={handleDialogClose}>
                <DialogTitle>No feedback found !</DialogTitle>
                <DialogContent>
                  <p>No feedback has been received for the selected lab from any trainees.</p>
                </DialogContent>
                <DialogActions>
                  <Tooltip title="Close dialog">
                    <span>
                      <Button onClick={handleDialogClose} color="primary" autoFocus sx={{ borderRadius: '50px' }}>
                        Ok
                      </Button>
                    </span>
                  </Tooltip>
                </DialogActions>
              </Dialog>
            </Grid>
        </Container>
      </Grid>
    </CardContent>
  );

  const renderStep2 = () => (
    <CardContent sx={{ pt: 0, pb: 2, px: 0 }}>
      <Grid container spacing={0}>
        <Container maxWidth={false}>
          <CardContent>
            <Grid container justify="center" spacing={0}>
              <Grid item xs={12}>
                <Card>
                  <Box sx={{ width: '100%', overflow: 'hidden', '&::-webkit-scrollbar': { display: 'none' }, scrollbarWidth: 'none' }}>
                    {feedbackRatingData.length > 0 ? (
                      <>
                        <DataGrid
                          rows={feedbackRatingData.map((rating, index) => ({
                            feedbackCount: rating.feedbackCount,
                            avgTrainingRating: rating.avgTrainingRating,
                            avgTrainerRating: rating.avgTrainerRating,
                            feedbackDate: feedbackDate[index],
                          }))}
                          getRowId={(row) => row.feedbackCount}
                          columns={[
                            {
                              field: 'radio',
                              headerName: 'Select Feedback',
                              flex: 0.8,
                              minWidth: 160,
                              align: 'center',
                              headerAlign: 'center',
                              renderCell: (params) => (
                                <Radio
                                  checked={selectedFeedbackId === params.row.feedbackCount}
                                  onChange={() => handleFeedbackSelection(params.row.feedbackCount)}
                                  value={params.row.feedbackCount}
                                  name="labRadio"
                                  inputProps={{ 'aria-label': params.row.feedbackCount }}
                                  disabled={isNaN(params.row.avgTrainingRating) && isNaN(params.row.avgTrainerRating)}
                                />
                              ),
                            },
                            { field: 'feedbackCount', headerName: 'Feedback Number', flex: 0.9, minWidth: 160, align: 'center', headerAlign: 'center' },
                            {
                              field: 'avgTrainingRating',
                              headerName: 'Avg Training Rating',
                              flex: 0.9,
                              minWidth: 160,
                              align: 'center',
                              headerAlign: 'center',
                              renderCell: (params) => (!isNaN(params.value) ? params.value : 'No feedback available'),
                            },
                            {
                              field: 'avgTrainerRating',
                              headerName: 'Avg Trainer Rating',
                              flex: 0.9,
                              minWidth: 160,
                              align: 'center',
                              headerAlign: 'center',
                              renderCell: (params) => (!isNaN(params.value) ? params.value : 'No feedback available'),
                            },
                            {
                              field: 'feedbackDate',
                              headerName: 'Feedback Date',
                              flex: 0.9,
                              minWidth: 160,
                              align: 'center',
                              headerAlign: 'center',
                            },
                            {
                              field: 'download',
                              headerName: 'Download',
                              flex: 0.6,
                              minWidth: 120,
                              align: 'center',
                              headerAlign: 'center',
                              renderCell: (params) => (
                                <Tooltip title="Download feedback summary (XLSX)">
                                  <span>
                                    <IconButton 
                                      onClick={(event) => handleTraineeDownload(event, params.row)}
                                      disabled={isNaN(params.row.avgTrainingRating) && isNaN(params.row.avgTrainerRating)}
                                    >
                                      <GetAppIcon />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              ),
                            },
                          ]}
                          autoHeight
                          hideFooter={true}
                          hideFooterPagination={true}
                          sx={{
                            width: '100%',
                            '& .MuiDataGrid-virtualScroller': { overflowX: 'hidden', overflowY: 'hidden' },
                            '& .MuiDataGrid-main': { overflow: 'hidden' },
                          }}
                        />
                        <Box sx={{ display: 'flex', justifyContent: 'flex-start', pl: { xs: 1, md: 2 } }}>
                          <TablePagination
                            component="div"
                            count={feedbackRatingData.length}
                            page={page}
                            rowsPerPage={rowsPerPage}
                            onPageChange={(event, newPage) => setPage(newPage)}
                            onRowsPerPageChange={(event) => {
                              setRowsPerPage(parseInt(event.target.value, 10));
                              setPage(0);
                            }}
                          />
                        </Box>
                      </>
                    ) : (
                      <Typography align="center" variant="body1">No Data Available</Typography>
                    )}
                  </Box>
                </Card>
              </Grid>
              <Grid container justify="center" spacing={0} style={{ marginTop: "10px", marginBottom: "10px" }}>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Tooltip title="Go back to previous step">
                      <span>
                        <Button
                          variant="contained"
                          color="primary"
                          type="button"
                          style={{ marginLeft: "10px" }}
                          onClick={handleFBBackClick}
                          sx={{ borderRadius: '50px' }}
                        >
                          Back
                        </Button>
                      </span>
                    </Tooltip>
                    <Tooltip title={selectedFeedbackId ? "View overall scores" : "Select a feedback to continue"}>
                      <span>
                        <Button
                          variant="contained"
                          color="primary"
                          type="button"
                          style={{ marginLeft: "10px" }}
                          disabled={!selectedFeedbackId}
                          onClick={handleContinue1}
                          sx={{ borderRadius: '50px' }}
                        >
                          Continue
                        </Button>
                      </span>
                    </Tooltip>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </CardContent>
        </Container>
      </Grid>
    </CardContent>
  );

  const renderStep3 = () => (
    <CardContent sx={{ pt: 0, pb: 2, px: 0 }}>
      <Grid container spacing={0}>
        <Container maxWidth={false}>
          <Typography
            sx={{ flex: "1 1 100%" }}
            style={{ fontSize: "x-large", lineHeight: "2.5" }}
            color="textPrimary"
            variant="body1"
          >
            Trainee Feedback
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 2 }}>
            <PieChart
              series={[
                {
                  data: [
                    { id: 0, value: selectedInstance[0]?.number_of_trainee || 0, label: 'Total Trainees' },
                    { id: 1, value: feedbackRatingData[selectedFeedbackId - 1]?.feedbackData.length || 0, label: 'Trainees Completed' }
                  ]
                }
              ]}
              width={400}
              height={300}
            />
            {feedbackRatingData[selectedFeedbackId - 1]?.feedbackData.length > 0 && (
              <BarChart
                xAxis={[{ scaleType: 'band', data: feedbackRatingData[selectedFeedbackId - 1].feedbackData.map(data => data.trainee_name) }]}
                series={[
                  { data: feedbackRatingData[selectedFeedbackId - 1].feedbackData.map(data => data.trainer_rating), label: 'Trainer Rating' },
                  { data: feedbackRatingData[selectedFeedbackId - 1].feedbackData.map(data => data.training_rating), label: 'Training Rating' }
                ]}
                width={400}
                height={300}
                yAxis={[
                  {
                    scaleType: 'linear',
                    domain: { min: 0, max: 5 },
                    label: 'Rating'
                  }
                ]}
              />
            )}
          </Box>
          <CardContent>
            <Grid container justify="center" spacing={0}>
              <Grid item xs={12}>
                <Card>
                  <Box sx={{ 
                    width: '100%', 
                    overflow: 'hidden',
                    '& .MuiDataGrid-root': {
                      minWidth: '100%'
                    }
                  }}>
                    {feedbackRatingData.length > 0 && feedbackRatingData[selectedFeedbackId - 1] ? (
                      <>
                        <DataGrid
                          rows={feedbackRatingData[selectedFeedbackId - 1].feedbackData.map((feedback, index) => ({
                            id: index + 1,
                            lab_id: feedback.lab_id,
                            feedback_no: feedback.feedback_no,
                            templateId: feedback.templateId,
                            trainee_name: feedback.trainee_name,
                            feedback_date: feedback.feedback_date,
                            trainer_rating: feedback.trainer_rating,
                            training_rating: feedback.training_rating,
                          }))}
                          columns={[
                            { 
                              field: 'id', 
                              headerName: 'S.No.', 
                              flex: 0.3, 
                              minWidth: 60, 
                              align: 'center', 
                              headerAlign: 'center' 
                            },
                            { 
                              field: 'lab_id', 
                              headerName: 'Lab Id', 
                              flex: 0.4, 
                              minWidth: 70, 
                              align: 'center', 
                              headerAlign: 'center' 
                            },
                            { 
                              field: 'feedback_no', 
                              headerName: 'Feedback No', 
                              flex: 0.5, 
                              minWidth: 100, 
                              align: 'center', 
                              headerAlign: 'center' 
                            },
                            { 
                              field: 'templateId', 
                              headerName: 'Template ID', 
                              flex: 0.5, 
                              minWidth: 90, 
                              align: 'center', 
                              headerAlign: 'center' 
                            },
                            { 
                              field: 'trainee_name', 
                              headerName: 'Trainee Name', 
                              flex: 0.8, 
                              minWidth: 120, 
                              align: 'center', 
                              headerAlign: 'center' 
                            },
                            { 
                              field: 'feedback_date', 
                              headerName: 'Feedback Date', 
                              flex: 0.6, 
                              minWidth: 110, 
                              align: 'center', 
                              headerAlign: 'center' 
                            },
                            { 
                              field: 'trainer_rating', 
                              headerName: 'Trainer Rating', 
                              flex: 0.5, 
                              minWidth: 100, 
                              align: 'center', 
                              headerAlign: 'center' 
                            },
                            { 
                              field: 'training_rating', 
                              headerName: 'Training Rating', 
                              flex: 0.5, 
                              minWidth: 100, 
                              align: 'center', 
                              headerAlign: 'center' 
                            },
                            {
                              field: 'download', 
                              headerName: 'Download', 
                              flex: 0.4, 
                              minWidth: 80, 
                              align: 'center', 
                              headerAlign: 'center',
                              renderCell: (params) => (
                                <Tooltip title="Download trainee feedback (XLSX)">
                                  <span>
                                    <IconButton 
                                      onClick={(event) => handleDataDownload(event, params.row)} 
                                      sx={{ borderRadius: '50px' }}
                                      size="small"
                                    >
                                      <GetAppIcon />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              )
                            },
                          ]}
                          autoHeight
                          hideFooter={true}
                          hideFooterPagination={true}
                          sx={{
                            width: '100%',
                            '& .MuiDataGrid-virtualScroller': { 
                              overflowX: 'auto',
                              overflowY: 'hidden' 
                            },
                            '& .MuiDataGrid-main': { 
                              overflow: 'hidden',
                              width: '100%'
                            },
                            '& .MuiDataGrid-cell': {
                              whiteSpace: 'normal',
                              wordWrap: 'break-word',
                            },
                          }}
                        />
                        <Box sx={{ display: 'flex', justifyContent: 'flex-start', pl: { xs: 1, md: 2 } }}>
                          <TablePagination
                            component="div"
                            count={feedbackRatingData[selectedFeedbackId - 1].feedbackData.length}
                            page={page}
                            rowsPerPage={rowsPerPage}
                            onPageChange={(event, newPage) => setPage(newPage)}
                            onRowsPerPageChange={(event) => {
                              setRowsPerPage(parseInt(event.target.value, 10));
                              setPage(0);
                            }}
                          />
                        </Box>
                      </>
                    ) : (
                      <Typography align="center" variant="body1">No Data Available</Typography>
                    )}
                  </Box>
                </Card>
              </Grid>
              <Grid container justify="center" spacing={0} style={{ marginTop: "10px", marginBottom: "10px" }}>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Tooltip title="Finish and return">
                      <span>
                        <Button
                          variant="contained"
                          color="primary"
                          type="button"
                          style={{ marginTop: '20px' }}
                          onClick={handleDone}
                          sx={{ borderRadius: '50px' }}
                        >
                          Done
                        </Button>
                      </span>
                    </Tooltip>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </CardContent>
        </Container>
      </Grid>
    </CardContent>
  );

  return (
    <React.Fragment>
      <Helmet>
        <title>{title} Feedback</title>
      </Helmet>
      <Box sx={{ pt: 1 }}>
        {currentStep === 1 && renderStep1()}
        {currentStep === 2 && selectedLabName && renderStep2()}
        {currentStep === 3 && selectedLabName && selectedFeedbackId && renderStep3()}
      </Box>
    </React.Fragment>
  );
}

